import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Web NFC tag reading, for "scan a tag to find its certificate".
 *
 * PLATFORM REALITY, stated up front because it decides the UI: `NDEFReader` is
 * Chromium-on-Android only. Safari implements Web NFC on no platform, so on an
 * iPhone a web page simply CANNOT start a scan — iOS reads tags at the OS level
 * and opens the tag's URL itself, which is how this app is normally reached in
 * the first place. `isSupported` is therefore load-bearing rather than
 * defensive: a caller must render a different affordance when it is false, not
 * a button that silently does nothing.
 *
 * What the tag holds is a URL, not a bare id — the canonical SUN/SDM form
 * `https://<install>/verify/<globalTagId>?proof=v1.<enc>.<cmac>` — so the id has
 * to be parsed back out of it. `globalTagId` corresponds to `tags.uid`, and the
 * certificate list's existing `query` parameter already matches `tag_uid` (VRG-286 — the
 * chip-UID column; `tag_id` is our own uuid FK now and would never match a scan), so a
 * scanned id can drive the normal filter with no new endpoint.
 *
 * NOTE ON AUTHENTICITY: reading a tag over Web NFC proves only that some tag was
 * present. The `proof` is what makes a scan *genuine*, and it is verified
 * server-side by `GET /verify/:global_tag_id` (VERA-455), which 404s on a failed
 * proof. This hook returns the proof alongside the id so a caller can pass it on;
 * it never asserts authenticity by itself.
 */

/** `/verify/<globalTagId>` — the same shape the scanner app parses. */
const VERIFY_PATH = /\/verify\/([^/?#]+)/;

export interface ParsedTag {
  /** `globalTagId` — corresponds to `tags.uid`. */
  tagId: string;
  /** SUN/SDM proof, when the tag carried one. Verified server-side, never here. */
  proof?: string;
  /** The raw URL read off the tag, for diagnostics. */
  url?: string;
}

export const parseTagUrl = (raw?: string | null): ParsedTag | null => {
  if (!raw) return null;
  const match = raw.match(VERIFY_PATH);
  if (!match) return null;
  let proof: string | undefined;
  try {
    proof = new URL(raw).searchParams.get('proof') ?? undefined;
  } catch {
    // A relative or malformed URL still yields a usable id from the regex above.
  }
  return { tagId: decodeURIComponent(match[1]), proof, url: raw };
};

/** Pull the first recognisable Vera tag URL out of an NDEF message. */
const readTag = (event: any): ParsedTag | null => {
  const records = event?.message?.records ?? [];
  for (const record of records) {
    let text: string | null = null;
    try {
      if (record.recordType === 'url' || record.recordType === 'absolute-url') {
        text = new TextDecoder().decode(record.data);
      } else if (record.recordType === 'text') {
        text = new TextDecoder(record.encoding || 'utf-8').decode(record.data);
      }
    } catch {
      // A record we can't decode is not a reason to abandon the whole message.
    }
    const parsed = parseTagUrl(text);
    if (parsed) return parsed;
  }
  // An unencoded or foreign tag still has a serial number. It won't carry a proof
  // and probably won't match a certificate, but returning it lets the caller say
  // "that tag isn't one of yours" instead of the uselessly vague "read failed".
  if (event?.serialNumber) return { tagId: String(event.serialNumber) };
  return null;
};

export interface VerifiedTag {
  certificateId: string;
  /** The scan-verification capability minted by a genuine tap (VERA-455). */
  vt: string | null;
}

/**
 * Run a scanned tag through the SAME server-side verification an iOS tap gets.
 *
 * `GET /verify/:global_tag_id` performs the SUN/SDM check and 404s on a bad
 * proof, then redirects to `<destination>?certificate_id=…&scan_id=…&vt=…`. It
 * returns a redirect rather than JSON because it exists for the phone-opens-the-
 * URL flow — but `fetch` follows redirects, and `response.url` is the FINAL url,
 * so the certificate id and the `vt` can both be read straight off it. No new
 * endpoint needed, and Android gets the identical guarantee iOS gets.
 *
 * Returns null when verification fails OR when the round-trip can't be made at
 * all (the tag's install host being a different origin without CORS). Null means
 * "not proven", never "no such certificate" — the caller must fall back to an
 * explicitly UNVERIFIED local match rather than treating null as authentic.
 */
export const resolveVerifiedTag = async (tagUrl?: string): Promise<VerifiedTag | null> => {
  if (!tagUrl) return null;
  try {
    const res = await fetch(tagUrl, { redirect: 'follow', credentials: 'include' });
    // A failed proof 404s before the certificate is ever resolved.
    if (!res.ok) return null;
    const final = new URL(res.url);
    const certificateId = final.searchParams.get('certificate_id');
    if (!certificateId) return null;
    return { certificateId, vt: final.searchParams.get('vt') };
  } catch {
    // Cross-origin install host, offline, blocked — all "not proven".
    return null;
  }
};

export type NfcScanStatus = 'idle' | 'scanning' | 'error';

export const useNfcScan = () => {
  const isSupported = typeof window !== 'undefined' && 'NDEFReader' in window;

  const [status, setStatus] = useState<NfcScanStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus('idle');
  }, []);

  // Never leave a reader armed behind a closed drawer or an unmounted page —
  // the scan holds a hardware session until it is aborted.
  useEffect(() => () => abortRef.current?.abort(), []);

  const scan = useCallback(async (): Promise<ParsedTag | null> => {
    if (!isSupported) {
      setError('This browser cannot scan NFC tags.');
      setStatus('error');
      return null;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setStatus('scanning');

    try {
      const reader = new (window as any).NDEFReader();
      // Triggers the permission prompt on first use, and throws if the user has
      // blocked NFC — both surface here rather than as a silent no-op.
      await reader.scan({ signal: controller.signal });

      const parsed = await new Promise<ParsedTag | null>((resolve, reject) => {
        reader.onreading = (event: any) => {
          const tag = readTag(event);
          if (tag) resolve(tag);
          else reject(new Error('That tag could not be read.'));
        };
        reader.onreadingerror = () => reject(new Error('That tag could not be read.'));
        // A cancel resolves null rather than rejecting: the user aborting is not
        // an error state and must not paint one.
        controller.signal.addEventListener('abort', () => resolve(null));
      });

      controller.abort();
      abortRef.current = null;
      setStatus('idle');
      return parsed;
    } catch (e: any) {
      abortRef.current = null;
      if (controller.signal.aborted) {
        setStatus('idle');
        return null;
      }
      setError(
        e?.name === 'NotAllowedError'
          ? 'Allow NFC access to scan a tag.'
          : e?.message || 'Could not scan that tag.',
      );
      setStatus('error');
      return null;
    }
  }, [isSupported]);

  return { isSupported, status, error, scan, cancel };
};
